/**
 * useSearchQuery
 */

import { useState, useCallback } from 'react';

import {
    Queries,
    ApolloLib,
} from 'bdvp-graphql';
import { useLang, useMediaConfigSearch } from 'bdvp-common/src/hooks/config';

import { LIMIT } from '../../utils/constants';
import { getContentPreference } from '../../utils/contentPreferences';
import useSearchQueryParam from './useSearchQueryParam';
import useSearchPaginationStore from './useSearchPaginationStore';
import useSelectedFiltersStore from './useSelectedFiltersStore';

const { useQuery } = ApolloLib;
const { SEARCH } = Queries;

const INITIAL_PAGE = 0;

const useSearchQuery = () => {

    const config = useMediaConfigSearch();
    const lang = useLang();
    const { searchQuery, queryType } = useSearchQueryParam();
    const {
        page,
        setPage,
    } = useSearchPaginationStore();
    const { selectedFilters } = useSelectedFiltersStore();
    const contentPreference = getContentPreference(config, selectedFilters);
    const [
        loadingMore,
        setLoadingMore,
    ] = useState(false);
    const limit = LIMIT;

    // Apollo Client v3 purposely does not return stale data in the data payload. (As it did in AC2)
    // They introduced previousData to get around this: https://github.com/apollographql/apollo-client/issues/6603
    const {
        loading,
        previousData,
        data = previousData, // Stale data is desired to support not changing total results count while sorting
        error,
        fetchMore,
    } = useQuery(
        SEARCH,
        {
            variables: {
                query: searchQuery,
                limit,
                page: INITIAL_PAGE,
                languagePreference: lang.toUpperCase(),
                contentPreference,
                queryType,
            },
            errorPolicy: 'all',
            ssr: false,
            skip: !searchQuery,
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'cache-first',
        });

    const fetchMoreData = useCallback(() => {

        const nextPage = page + 1;

        setLoadingMore(true);

        return fetchMore({
            variables: {
                page: nextPage,
            },
            updateQuery: (prev, { fetchMoreResult }) => {

                setLoadingMore(false);
                setPage(nextPage);

                if (!fetchMoreResult) return prev;

                return Object.assign({}, prev, {
                    search: {
                        plays: [
                            ...prev.search.plays,
                            ...fetchMoreResult.search.plays,
                        ],
                        total: fetchMoreResult.search.total,
                        __typename: 'PagedClipDetails',
                    },
                });

            },
        });

    }, [
        fetchMore,
        page,
        setPage,
    ]);

    return {
        loading,
        loadingMore,
        data,
        error,
        fetchMoreData,
        page,
        limit,
        contentPreference,
    };

};

export default useSearchQuery;
